import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { exitViewAs, useViewAs, useViewAsStore } from '@/lib/view-as';

/**
 * G-08 — the amber strip across the top of the shell while the Owner is
 * previewing another person: "Viewing as <name> — Exit". Persistent (it is
 * drawn by the frame, so no screen can lose it), `role="status"` so a screen
 * reader hears it arrive, and the Exit button restores the Owner's own view.
 *
 * THE PREVIEW IS READ-ONLY AND THE SERVER SESSION IS NEVER CHANGED — the
 * tooltip says so in the person's language, because the one dangerous
 * misreading of this strip is "I am signed in as Omar". Nothing is: every
 * request is still the Owner's, and writes are refused in the browser before
 * they leave (`apps/web/src/api.ts`). When one is refused, the sentence
 * appears here as an alert beside the name until the preview ends.
 */
export function ViewAsBanner() {
  const { t } = useTranslation();
  const target = useViewAs();
  const blocked = useViewAsStore((state) => state.blocked);
  if (target === null) return null;

  return (
    <div
      role="status"
      data-testid="view-as-banner"
      title={t('viewAs.tooltip', { name: target.name })}
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-100 px-6 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
    >
      <Eye className="size-4 shrink-0" aria-hidden />
      <span className="font-semibold">{t('viewAs.banner', { name: target.name })}</span>
      {blocked > 0 ? <span role="alert">{t('viewAs.readOnly', { name: target.name })}</span> : null}
      <button
        type="button"
        onClick={exitViewAs}
        className="ms-auto rounded-md border border-current px-3 py-1 text-xs font-semibold underline-offset-2 hover:underline"
      >
        {t('viewAs.exit')}
      </button>
    </div>
  );
}
